import {
  Badge,
  Box,
  Center,
  Heading,
  HStack,
  IconButton,
  IconButtonWithTooltip,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { useEditPocContactDetailsApi } from 'apis/poc-agent-calls/use-edit-poc-contact-details-api';
import { useGetPocAgentCallAnalysisApi } from 'apis/poc-agent-calls/use-get-poc-agent-call-analysis-api';
import { useGetPocAgentCallTranscriptApi } from 'apis/poc-agent-calls/use-get-poc-agent-call-transcript-api';
import React, { useState } from 'react';
import { FiCheck, FiEdit, FiX } from 'react-icons/fi';
import { PocCallAnalysisIcon } from '../icons/poc-call-analysis-icon';
import { PocCallTranscriptIcon } from '../icons/poc-call-transcript-icon';
import { PocInternalFeedbackIcon } from '../icons/poc-internal-feedback-icon';

const PocCallAnalysisButton = React.memo(({ data }: IPocActionsCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    data: analysisData,
    refetch: refetchCallAnalysis,
    isLoading: isLoadingAnalysis,
  } = useGetPocAgentCallAnalysisApi(data.id, { enabled: false });
  const { mutateAsync: editContactDetails, isLoading: isEditingContactDetails } = useEditPocContactDetailsApi();

  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactDetailsText, setContactDetailsText] = useState('');

  const handleOpen = async () => {
    onOpen();
    await refetchCallAnalysis();
  };

  const handleClose = () => {
    setIsEditingContact(false);
    setContactDetailsText('');
    onClose();
  };

  const handleEditContact = () => {
    setContactDetailsText(analysisData?.contactDetailsText || '');
    setIsEditingContact(true);
  };

  const handleCancelEdit = () => {
    setIsEditingContact(false);
    setContactDetailsText('');
  };

  const handleSaveContact = async () => {
    try {
      await editContactDetails({
        pocCallId: data.id,
        body: {
          contactDetails: contactDetailsText,
        },
      });

      setIsEditingContact(false);
      setContactDetailsText('');

      await refetchCallAnalysis();
    } catch (error) {
      console.error('Failed to update contact details:', error);
    }
  };

  return (
    <Box pos='relative'>
      <IconButtonWithTooltip
        label='Call Analysis'
        aria-label='call-analysis'
        Icon={<PocCallAnalysisIcon />}
        variant='unstyled'
        onClick={handleOpen}
      />

      <Modal isOpen={isOpen} onClose={handleClose} scrollBehavior='inside' size='4xl' id={`analysis-modal-${data.id}`}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Call Analysis</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoadingAnalysis ? (
              <Center py={8}>
                <Text>Loading analysis...</Text>
              </Center>
            ) : (
              <VStack spacing={6} align='stretch'>
                <Box>
                  <Heading size='md' mb={3} color='gray.700'>
                    Call Summary
                  </Heading>
                  <Text p={4} bg='gray.50' borderRadius='md' fontSize='md' lineHeight='tall'>
                    {analysisData?.summary || 'No summary available'}
                  </Text>
                </Box>

                <Box>
                  <Heading size='md' mb={3} color='gray.700'>
                    Sentiment Analysis
                  </Heading>
                  <Badge
                    colorScheme={
                      analysisData?.sentiment?.toLowerCase().includes('positive')
                        ? 'green'
                        : analysisData?.sentiment?.toLowerCase().includes('negative')
                        ? 'red'
                        : 'yellow'
                    }
                    fontSize='md'
                    px={4}
                    py={2}
                    borderRadius='md'>
                    {analysisData?.sentiment || 'Neutral'}
                  </Badge>
                </Box>

                <Box>
                  <Heading size='md' mb={3} color='gray.700'>
                    Call Success
                  </Heading>
                  <Badge
                    colorScheme={analysisData?.isSuccessful ? 'green' : 'red'}
                    fontSize='md'
                    px={4}
                    py={2}
                    borderRadius='md'>
                    {analysisData?.isSuccessful ? 'Successful' : 'Unsuccessful'}
                  </Badge>
                </Box>

                <Box>
                  <HStack justify='space-between' align='center' mb={3}>
                    <Heading size='md' color='gray.700'>
                      Contact Details
                    </Heading>
                    {!isEditingContact && (
                      <IconButton
                        aria-label='Edit contact details'
                        icon={<FiEdit />}
                        size='sm'
                        variant='ghost'
                        onClick={handleEditContact}
                      />
                    )}
                  </HStack>

                  {isEditingContact ? (
                    <VStack spacing={3} align='stretch'>
                      <Textarea
                        value={contactDetailsText}
                        onChange={(e) => setContactDetailsText(e.target.value)}
                        placeholder='Enter contact details...'
                        rows={4}
                        resize='vertical'
                      />
                      <HStack spacing={2}>
                        <IconButton
                          aria-label='Save contact details'
                          icon={<FiCheck />}
                          size='sm'
                          colorScheme='green'
                          onClick={handleSaveContact}
                          isLoading={isEditingContactDetails}
                        />
                        <IconButton
                          aria-label='Cancel edit'
                          icon={<FiX />}
                          size='sm'
                          colorScheme='red'
                          variant='ghost'
                          onClick={handleCancelEdit}
                        />
                      </HStack>
                    </VStack>
                  ) : (
                    <Text p={4} bg='gray.50' borderRadius='md' fontSize='md' whiteSpace='pre-wrap'>
                      {analysisData?.contactDetailsText || 'No contact details available'}
                    </Text>
                  )}
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

const PocTranscriptButton = React.memo(({ data }: IPocActionsCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    data: transcriptData,
    refetch: refetchCallTranscript,
    isLoading: isLoadingTranscript,
  } = useGetPocAgentCallTranscriptApi(data.id, { enabled: false });

  const handleOpen = async () => {
    onOpen();
    await refetchCallTranscript();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Box pos='relative'>
      <IconButtonWithTooltip
        label='Call Transcript'
        aria-label='call-transcript'
        Icon={<PocCallTranscriptIcon />}
        variant='unstyled'
        onClick={handleOpen}
      />

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        scrollBehavior='inside'
        size='4xl'
        id={`transcript-modal-${data.id}`}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Call Transcript</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoadingTranscript ? (
              <Center py={8}>
                <Text>Loading transcript...</Text>
              </Center>
            ) : (
              <VStack spacing={4} align='stretch'>
                {Array.isArray(transcriptData) && transcriptData.length > 0 ? (
                  transcriptData.map((item, index) => (
                    <Box
                      key={index}
                      bg={item.role === 'user' ? 'blue.50' : 'gray.50'}
                      p={4}
                      borderRadius='md'
                      maxW='80%'
                      alignSelf={item.role === 'user' ? 'flex-end' : 'flex-start'}>
                      <Text fontSize='xs' color='gray.500' mb={2} textTransform='capitalize'>
                        {item.role}
                      </Text>
                      <Text fontSize='md' whiteSpace='pre-wrap'>
                        {item.content}
                      </Text>
                    </Box>
                  ))
                ) : (
                  <Text textAlign='center' color='gray.500'>
                    No transcript available
                  </Text>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

const PocInternalFeedbackButton = React.memo(({ data }: IPocActionsCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Box pos='relative'>
      {!!data.internalFeedback && (
        <Center
          pos='absolute'
          top={0}
          right={0}
          bgColor='green.500'
          h='15px'
          minW='15px'
          fontSize='2xs'
          color='white'
          borderRadius='full'
          zIndex={1}
          px='4px'
          pt='1px'
          shadow='md'></Center>
      )}

      <IconButtonWithTooltip
        label='Internal Feedback'
        aria-label='internal-feedback'
        Icon={<PocInternalFeedbackIcon />}
        variant='unstyled'
        onClick={handleOpen}
      />

      <Modal isOpen={isOpen} onClose={handleClose} scrollBehavior='inside' size='4xl' id={`feedback-modal-${data.id}`}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Internal Feedback (AI Generated)</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align='stretch'>
              <Box>
                <Text
                  p={4}
                  bg='gray.50'
                  borderRadius='md'
                  fontSize='md'
                  lineHeight='tall'
                  whiteSpace='pre-wrap'
                  minH='200px'>
                  {data.internalFeedback || 'No internal feedback available'}
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

export interface IPocActionsCell {
  data: {
    id: number;
    internalFeedback?: string | null;
  };
}

export const PocActionsCell = ({ data }: IPocActionsCell) => {
  return (
    <HStack>
      <PocCallAnalysisButton data={data} />
      <PocTranscriptButton data={data} />
      <PocInternalFeedbackButton data={data} />
    </HStack>
  );
};
